import React from 'react';
import { Link } from 'react-router-dom';
import PublicShell from './PublicShell';
import { PRODUCT_DESCRIPTOR, PRODUCT_NAME, TAGLINE } from '../../config/appBranding';
import './LandingPage.css';

/**
 * Public landing page. Unauthenticated visitors get here at `/`.
 * Authed users are bounced straight into the app via RedirectIfAuthed.
 */
export default function LandingPage() {
  return (
    <PublicShell>
      <div className="landing">
        <section className="landing__hero">
          <div className="landing__hero-copy">
            <span className="landing__eyebrow">{PRODUCT_DESCRIPTOR}</span>
            <h1 className="landing__headline">
              {TAGLINE}
            </h1>
            <p className="landing__subhead">
              {PRODUCT_NAME} turns KPIs, attendance, approvals, and executive digests into one clean operating system for multi-location leaders.
            </p>
            <div className="landing__cta">
              <Link to="/login?workspace=demo-company" className="btn btn--primary">Open Demo Workspace</Link>
              <Link to="/demo" className="btn btn--secondary">Book a Demo</Link>
            </div>
            <div className="landing__proof" aria-label="Product highlights">
              <span>Private tenant workspace</span>
              <span>Executive Snapshot first</span>
              <span>No public passwords shown</span>
            </div>
          </div>

          <div className="landing__hero-visual" aria-label={`${PRODUCT_NAME} dashboard preview`}>
            <MockDashboard />
          </div>
        </section>

        <section className="landing__logos" aria-label="Built for operational teams">
          <span>Built for operators managing</span>
          <strong>branches</strong>
          <strong>districts</strong>
          <strong>stations</strong>
          <strong>regions</strong>
        </section>

        <section className="landing__section">
          <div className="landing__section-heading">
            <span className="landing__section-kicker">One source of truth</span>
            <h2>Built for the way operations actually runs.</h2>
          </div>
          <div className="landing__tiles">
            <Tile
              eyebrow="Team members"
              title="See your numbers without the spreadsheet."
              body="Your goals, your actuals, and where you stand, refreshed daily. No more digging through reports on Monday morning."
            />
            <Tile
              eyebrow="Location leaders"
              title="One view across every location you run."
              body="Compare locations, spot who's behind, and approve vacation requests without flipping between tools or inboxes."
            />
            <Tile
              eyebrow="Executives"
              title="A 30-second read on the whole portfolio."
              body="Executive Snapshot shows the pulse, top metrics, and what needs attention, delivered to your inbox every Monday."
            />
          </div>
        </section>

        <section className="landing__split">
          <div className="landing__section-heading landing__section-heading--left">
            <span className="landing__section-kicker">Control without clutter</span>
            <h2>Every location gets clarity. Corporate gets confidence.</h2>
            <p>
              Keep new companies and new locations simple at launch, then unlock the deeper governance tools once the workspace is ready.
            </p>
          </div>
          <div className="landing__workflow">
            <WorkflowStep step="01" title="Connect your locations" body="Create the company workspace, add locations, and assign leaders." />
            <WorkflowStep step="02" title="Choose the KPI template" body="Start blank or use the company KPI template for fast, consistent setup." />
            <WorkflowStep step="03" title="Launch the snapshot" body="Activate dashboards only when the required goals and owners are ready." />
          </div>
        </section>

        <section className="landing__section landing__section--platform">
          <div className="landing__section-heading">
            <span className="landing__section-kicker">What's inside</span>
            <h2>The operating layer between strategy and the front line.</h2>
          </div>
          <div className="landing__features">
            <Feature
              title="KPI tracking"
              body="Monthly, quarterly, and yearly goals with automatic status rollups."
            />
            <Feature
              title="Attendance health"
              body="At-work rate, risk lists, and manager-level visibility in one place."
            />
            <Feature
              title="Vacation approvals"
              body="Coverage rules, blackout dates, and max-managers-off enforcement."
            />
            <Feature
              title="Alerts Center"
              body="Every KPI miss, approval backlog, and at-risk manager ranked by urgency."
            />
            <Feature
              title="Executive digest"
              body="A weekly pulse of wins and watch items delivered to leadership."
            />
            <Feature
              title="Audit trail"
              body="Approvals, overrides, and changes stay linked to the history that matters."
            />
          </div>
        </section>

        <section className="landing__cta-band">
          <span className="landing__eyebrow">Founding customer access</span>
          <h2>Ready to see {PRODUCT_NAME} on your data?</h2>
          <p>
            We're working with a small group of founding customers. Book time with us and we'll walk through the product on your actual numbers.
          </p>
          <div className="landing__cta">
            <Link to="/login?workspace=demo-company" className="btn btn--primary">Open Demo Workspace</Link>
            <a href="mailto:sales@vizionops.com" className="btn btn--secondary btn--light">Contact Sales</a>
          </div>
        </section>
      </div>
    </PublicShell>
  );
}

function Tile({ eyebrow, title, body }) {
  return (
    <div className="tile">
      <span className="tile__eyebrow">{eyebrow}</span>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function Feature({ title, body }) {
  return (
    <div className="feature">
      <div className="feature__mark" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function WorkflowStep({ step, title, body }) {
  return (
    <div className="workflow-step">
      <span>{step}</span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </div>
  );
}

function MockDashboard() {
  return (
    <div className="mock">
      <div className="mock__topbar">
        <div>
          <span className="mock__label">Executive Snapshot</span>
          <strong>Company Rollup · April</strong>
        </div>
        <span className="mock__chip">Live</span>
      </div>
      <div className="mock__body">
        <div className="mock__metric mock__metric--hero">
          <span>Portfolio pulse</span>
          <strong>91%</strong>
          <small>14 locations on track</small>
        </div>
        <MockMetric label="At-work rate" value="94%" tone="green" />
        <MockMetric label="Pending approvals" value="6" tone="amber" />
        <MockMetric label="Managers at risk" value="2" tone="red" />
        <div className="mock__panel">
          <div className="mock__panel-header">
            <strong>Scorecard highlights</strong>
            <span>Ranked by gap</span>
          </div>
          <MockBar label="Labor efficiency" value="78%" width="78" tone="amber" />
          <MockBar label="Revenue / day" value="$14.2k" width="96" tone="green" />
          <MockBar label="Customer satisfaction" value="92%" width="92" tone="green" />
        </div>
        <div className="mock__panel mock__panel--attention">
          <div className="mock__panel-header">
            <strong>Needs attention</strong>
            <span>3 items</span>
          </div>
          <div className="mock__alert">Response time behind goal</div>
          <div className="mock__alert">Two approvals waiting</div>
        </div>
      </div>
    </div>
  );
}

function MockMetric({ label, value, tone }) {
  return (
    <div className={`mock__metric mock__metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MockBar({ label, value, width, tone }) {
  return (
    <div className="mock__bar-row">
      <div className="mock__bar-label">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="mock__bar-track">
        <div className={`mock__bar-fill mock__bar-fill--${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}
